/**
 * Pre-flight balance validation for blockchain transactions.
 *
 * Checks that the user has sufficient native BNB (for gas + value)
 * and/or sufficient ERC20 token balance before submitting a transaction.
 *
 * Asset decimals:
 *   BNB: 18 (native token)
 *   WELEPHANT: 9
 *   stWELEPHANT: 18
 */

import { formatEtherLimited } from './ethersUtils'
import { formatUnits } from 'ethers'

// Conservative gas cost estimate for BSC: ~500k gas × 5 gwei = 0.0025 BNB
const ESTIMATED_GAS_COST = 2_500_000_000_000_000n // 0.0025 BNB

interface ValidateBalanceParams {
  /** User's native BNB balance (18 decimals) */
  balance: bigint | undefined
  /** BNB value being sent with the transaction (18 decimals, default 0) */
  value?: bigint
  /** ERC20 token balance in the token's native decimals */
  tokenBalance?: bigint
  /** ERC20 token amount required (same decimals as tokenBalance) */
  tokenAmount?: bigint
  /** Token symbol for error message, e.g. "WELEPHANT" */
  tokenSymbol?: string
  /** Token decimals for formatting in error messages (9 for WELEPHANT, 18 for stWELEPHANT) */
  tokenDecimals?: number
}

interface ValidationResult {
  valid: boolean
  error: string | null
}

/**
 * Validate that the user has sufficient balance before submitting a transaction.
 * Call this after computing amounts but before calling contract.connect(signer).method().
 */
export function validateBalance(params: ValidateBalanceParams): ValidationResult {
  const {
    balance,
    value = 0n,
    tokenBalance,
    tokenAmount,
    tokenSymbol = 'tokens',
    tokenDecimals = 18,
  } = params

  if (balance === undefined) {
    return { valid: false, error: 'Wallet not connected' }
  }

  // Check native BNB: value + estimated gas
  const requiredBNB = value + ESTIMATED_GAS_COST
  if (balance < requiredBNB) {
    const have = formatEtherLimited(balance)
    const need = formatEtherLimited(requiredBNB)
    if (value > 0n) {
      return {
        valid: false,
        error: `Insufficient BNB balance. Need ${need} BNB (including gas), have ${have} BNB.`,
      }
    }
    return {
      valid: false,
      error: `Insufficient BNB for gas fees. Need ~${need} BNB, have ${have} BNB.`,
    }
  }

  // Check ERC20 token balance if provided
  if (tokenBalance !== undefined && tokenAmount !== undefined && tokenBalance < tokenAmount) {
    const have = formatTokenLimited(tokenBalance, tokenDecimals)
    const need = formatTokenLimited(tokenAmount, tokenDecimals)
    return {
      valid: false,
      error: `Insufficient ${tokenSymbol} balance. Have ${have}, need ${need}.`,
    }
  }

  return { valid: true, error: null }
}

/**
 * Check if an input amount exceeds the available balance.
 * Pure client-side comparison for real-time input field styling.
 * No RPC calls — compares against already-cached balance values.
 */
export function isAmountExceedingBalance(
  inputAmount: string,
  balance: bigint,
  decimals: number = 18,
): boolean {
  if (!inputAmount || balance <= 0n) return false
  const parsed = parseFloat(inputAmount)
  if (!parsed || parsed <= 0) return false
  try {
    const balanceFloat = parseFloat(formatUnits(balance, decimals))
    return parsed > balanceFloat
  } catch {
    return false
  }
}

/** Format a token amount with limited decimals for error messages */
function formatTokenLimited(value: bigint, decimals: number): string {
  if (decimals === 18) return formatEtherLimited(value)
  const formatted = parseFloat(formatUnits(value, decimals))
  if (formatted === 0) return '0'
  if (formatted < 0.00001) return '<0.00001'
  const truncated = Math.floor(formatted * 100000) / 100000
  return truncated.toFixed(5).replace(/\.?0+$/, '') || '0'
}
